/**
 * The main window's Pop out control (top bar): pop out the page on screen or
 * one of the large modules into its own window, restore the last pop-outs,
 * close them all, and (optional) reopen them when Velora opens. Also hosts
 * the "pop-ups blocked" hint and the opener side of "Show in main window".
 *
 * Nav links are real URLs (TanStack <Link>), so Ctrl/Cmd-click, middle-click
 * and Shift-click open any module in a new tab or window with the full shell.
 */
import { useNavigate } from '@tanstack/react-router';
import { Button, Menu, type MenuEntry } from '@velora/ui';
import { AppWindow, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOfficeScope } from '../office/OfficeScope.js';
import { useCurrentPatient } from '../patient/current.js';
import { useActiveRoute } from '../layout/activeRoute.js';
import { clearBlocked, openPopout, removeBlocked, useBlockedPopouts } from './open.js';
import { POPOUT_MODULES, popoutSpec, specHref, type PopoutSpec } from './popout.js';
import { closeAllPopouts, useOpenPopouts } from './WindowSync.js';
import {
  lastWorkspace,
  readPinnedDefault,
  readReopen,
  readWorkspace,
  saveReopen,
  type WorkspaceEntry,
} from './workspace.js';
import './windows.css';

export interface PopoutMenuProps {
  /** Whether this person may open a route (permission in the offices in scope). */
  canOpen: (routePath: string) => boolean;
}

function routePathOf(spec: PopoutSpec, patientId: string | null): string {
  return patientId && spec.patientPath ? (spec.patientPath.split('?')[0] ?? spec.path) : spec.path;
}

/** Opens the workspace's pop-outs; returns how many the browser blocked. */
function restore(entries: readonly WorkspaceEntry[], patientId: string | null, office: string) {
  let blocked = 0;
  for (const e of entries) {
    const spec = popoutSpec(e.key);
    const href = e.path ?? (spec ? specHref(spec, e.pinned ? null : patientId) : null);
    if (!href) continue;
    const ok = openPopout({
      key: e.key,
      label: spec?.label ?? e.key,
      href,
      office,
      pinned: e.pinned && Boolean(patientId),
    });
    if (!ok) blocked++;
  }
  return blocked;
}

/** Restores the workspace once per tab, when "Reopen my pop-outs" is on. */
let triedReopen = false;

export function PopoutMenu({ canOpen }: PopoutMenuProps) {
  const active = useActiveRoute();
  const office = useOfficeScope();
  const patient = useCurrentPatient();
  const patientId = patient?.id ?? null;
  const open = useOpenPopouts();
  const [reopen, setReopen] = useState(readReopen);
  // Re-reads the workspace each time the menu is opened.
  const [tick, setTick] = useState(0);

  const openKeysRef = useRef<string[]>([]);
  openKeysRef.current = open.map((o) => o.key);

  // Ask once per tab; a page reload of this tab must not open them twice.
  const officeRef = useRef(office.param);
  officeRef.current = office.param;
  useEffect(() => {
    if (triedReopen || !readReopen()) return undefined;
    triedReopen = true;
    try {
      if (window.sessionStorage.getItem('velora.win.v1.reopened') === '1') return undefined;
      window.sessionStorage.setItem('velora.win.v1.reopened', '1');
    } catch {
      // No session storage: still try once for this page load.
    }
    // Give open pop-outs a moment to answer `who` so none is opened twice.
    const t = window.setTimeout(() => {
      const openKeys = new Set(openKeysRef.current);
      const todo = lastWorkspace(readWorkspace(), Date.now()).filter((e) => !openKeys.has(e.key));
      if (todo.length > 0) restore(todo, null, officeRef.current);
    }, 800);
    return () => window.clearTimeout(t);
  }, []);

  const items = useMemo<MenuEntry[]>(() => {
    const entries: MenuEntry[] = [];
    if (active) {
      const key = active.moduleId;
      const spec = popoutSpec(key);
      entries.push({
        id: 'pop-here',
        label: `Pop out ${active.route.title}`,
        onSelect: () => {
          const here = window.location;
          const params = new URLSearchParams(here.search);
          params.delete('office');
          const q = params.toString();
          openPopout({
            key,
            label: spec?.label ?? active.route.title,
            href: `${here.pathname}${q ? `?${q}` : ''}`,
            office: office.param,
            pinned: readPinnedDefault(key),
          });
        },
      });
      entries.push({ type: 'separator', id: 's-here' });
    }
    entries.push({ type: 'label', id: 'l-mods', label: 'Open in a new window' });
    for (const spec of POPOUT_MODULES) {
      const forPatient = Boolean(patientId && spec.patientPath);
      if (!canOpen(routePathOf(spec, forPatient ? patientId : null))) continue;
      const isOpen = open.some((o) => o.key === spec.key);
      entries.push({
        id: `pop-${spec.key}`,
        label: `${spec.label}${forPatient ? ' (selected patient)' : ''}`,
        ...(isOpen ? { badge: 'open' } : {}),
        onSelect: () =>
          void openPopout({
            key: spec.key,
            label: spec.label,
            href: specHref(spec, patientId),
            office: office.param,
            pinned: forPatient && readPinnedDefault(spec.key),
          }),
      });
    }
    const last = lastWorkspace(readWorkspace(), Date.now()).filter(
      (e) => !open.some((o) => o.key === e.key),
    );
    entries.push({ type: 'separator', id: 's-ws' });
    entries.push({
      id: 'restore',
      label: last.length > 0 ? `Restore my workspace (${last.length})` : 'Restore my workspace',
      disabled: last.length === 0,
      title:
        last.length === 0
          ? 'Nothing to restore: no pop-out windows were closed recently'
          : last.map((e) => popoutSpec(e.key)?.label ?? e.key).join(', '),
      onSelect: () => void restore(last, patientId, office.param),
    });
    entries.push({
      id: 'close-all',
      label: open.length > 0 ? `Close all pop-outs (${open.length})` : 'Close all pop-outs',
      disabled: open.length === 0,
      onSelect: closeAllPopouts,
    });
    entries.push({
      type: 'checkbox',
      id: 'reopen',
      label: 'Reopen my pop-outs when Velora opens',
      checked: reopen,
      onCheckedChange: (c) => {
        saveReopen(c);
        setReopen(c);
      },
    });
    return entries;
    // `tick`: the workspace is read from storage when the menu opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, office.param, patientId, open, canOpen, reopen, tick]);

  return (
    <Menu
      align="end"
      items={items}
      trigger={
        <button
          type="button"
          className="shell-iconbtn shell-popout-btn"
          aria-label={
            open.length > 0
              ? `Pop out windows: ${open.length} open`
              : 'Pop out: open a module in its own window'
          }
          title="Pop out: open this page or a module in its own window (split screen, second monitor)"
          onPointerDown={() => setTick((n) => n + 1)}
          data-testid="popout-menu"
        >
          <AppWindow size={17} aria-hidden />
          {open.length > 0 ? (
            <span className="shell-popout-btn__count" aria-hidden>
              {open.length}
            </span>
          ) : null}
        </button>
      }
    />
  );
}

/**
 * The opener side of a pop-out's "Show in main window": the pop-out posts
 * the page to this window (same origin only).
 */
export function OpenInMainListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const on = (ev: MessageEvent<unknown>) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { t?: unknown; href?: unknown } | null;
      if (!d || d.t !== 'velora-open-main' || typeof d.href !== 'string') return;
      if (!d.href.startsWith('/') || d.href.startsWith('//')) return;
      const url = new URL(d.href, window.location.origin);
      void navigate({
        to: url.pathname,
        search: Object.fromEntries(url.searchParams),
      } as never);
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [navigate]);
  return null;
}

/**
 * Shown when the browser blocked pop-out windows (a restore opens several at
 * once, which needs pop-ups allowed for this site; a single click opens one).
 */
export function PopoutBlockedHint() {
  const blocked = useBlockedPopouts();
  if (blocked.length === 0) return null;
  return (
    <div className="shell-popblocked" role="alert" data-testid="popout-blocked">
      <div className="shell-popblocked__head">
        <AppWindow size={16} aria-hidden />
        <strong>
          {blocked.length === 1
            ? 'Your browser blocked a pop-out window'
            : `Your browser blocked ${blocked.length} pop-out windows`}
        </strong>
        <button
          type="button"
          className="shell-iconbtn shell-popblocked__close"
          aria-label="Dismiss"
          onClick={clearBlocked}
        >
          <X size={14} aria-hidden />
        </button>
      </div>
      <p className="shell-popblocked__text">
        To open several at once, allow pop-ups for this site: click the blocked pop-up icon at the
        right of the address bar and choose Always allow. Or open them one at a time:
      </p>
      <div className="shell-popblocked__actions">
        {blocked.map((b) => (
          <Button
            key={b.key}
            variant="secondary"
            size="sm"
            onClick={() => {
              if (openPopout(b)) removeBlocked(b.key);
            }}
          >
            Open {b.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
